export type TocEntry = {
  level: 2 | 3;
  id: string;
  text: string;
};

const TOC_HEADING_LABELS = new Set(['目次', 'もくじ']);

export function isTocHeadingLabel(text: string): boolean {
  return TOC_HEADING_LABELS.has(text.trim());
}

export function makeHeadingId(text: string, used: Set<string>): string {
  let base = text
    .trim()
    .normalize('NFKC')
    .replace(/[\s　]+/g, '-')
    .replace(/[^\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}a-zA-Z0-9_-]/gu, '')
    .slice(0, 60);

  if (!base) {
    base = 'section';
  }

  let id = base;
  let suffix = 2;
  while (used.has(id)) {
    id = `${base}-${suffix}`;
    suffix += 1;
  }
  used.add(id);
  return id;
}

/**
 * 本文HTMLに埋め込まれた手動目次（nav.toc / h2目次+リスト）を除去する。
 * サイト側で自動生成するため、重複表示を防ぐ。
 */
export function stripEmbeddedTableOfContents(html: string): string {
  if (!html || typeof DOMParser === 'undefined') {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const root = doc.body;

  root.querySelectorAll('nav.toc, nav[class*="toc"]').forEach((el) => el.remove());

  [...root.querySelectorAll('h2, h3')].forEach((heading) => {
    const label = heading.textContent?.trim() ?? '';
    if (!isTocHeadingLabel(label)) {
      return;
    }

    const list = heading.nextElementSibling;
    heading.remove();
    if (list && (list.tagName === 'OL' || list.tagName === 'UL')) {
      list.remove();
    }
  });

  return root.innerHTML;
}

export function prepareArticleHtml(html: string): { html: string; toc: TocEntry[] } {
  const stripped = stripEmbeddedTableOfContents(html);
  if (!stripped || typeof DOMParser === 'undefined') {
    return { html: stripped, toc: [] };
  }

  const doc = new DOMParser().parseFromString(stripped, 'text/html');
  const root = doc.body;
  const usedIds = new Set<string>();
  const toc: TocEntry[] = [];

  root.querySelectorAll('h2, h3').forEach((el) => {
    const text = el.textContent?.trim() ?? '';
    if (!text || isTocHeadingLabel(text)) {
      return;
    }

    const level: 2 | 3 = el.tagName === 'H2' ? 2 : 3;
    if (!el.id) {
      el.id = makeHeadingId(text, usedIds);
    } else {
      usedIds.add(el.id);
    }

    toc.push({ level, id: el.id, text });
  });

  return { html: root.innerHTML, toc };
}

export function extractMarkdownHeadings(markdown: string): TocEntry[] {
  const usedIds = new Set<string>();
  const toc: TocEntry[] = [];

  for (const line of markdown.split('\n')) {
    const h2 = line.match(/^##\s+(.+)$/);
    const h3 = line.match(/^###\s+(.+)$/);

    if (h2) {
      const text = h2[1].trim();
      if (isTocHeadingLabel(text)) {
        continue;
      }
      toc.push({ level: 2, id: makeHeadingId(text, usedIds), text });
    } else if (h3) {
      const text = h3[1].trim();
      if (isTocHeadingLabel(text)) {
        continue;
      }
      toc.push({ level: 3, id: makeHeadingId(text, usedIds), text });
    }
  }

  return toc;
}

export function stripEmbeddedMarkdownToc(markdown: string): string {
  const lines = markdown.split('\n');
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const h2 = lines[i].match(/^##\s+(.+)$/);
    if (h2 && isTocHeadingLabel(h2[1])) {
      i += 1;
      while (i < lines.length && /^(\s*[-*]|\s*\d+\.)/.test(lines[i])) {
        i += 1;
      }
      continue;
    }
    result.push(lines[i]);
    i += 1;
  }

  return result.join('\n');
}

export function shouldShowAutoToc(toc: TocEntry[]): boolean {
  return toc.some((entry) => entry.level === 2);
}

/** 目次表示用（h2 のみ。h3 は本文に残すが目次には出さない） */
export function getTocDisplayEntries(entries: TocEntry[]): TocEntry[] {
  return entries.filter((entry) => entry.level === 2);
}
